import { useMemo } from 'react'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { NumberFormatter, useMantineTheme } from '@mantine/core'
import { tableDefaultAlertBannerProps, tableDefaultProps } from '@shared/components/table/table'
import { NoDataMessage } from '@shared/components/table/table-no-data-message'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { CreditorHistoryDetail } from '@shared/types'
import { keyToLabelMap } from './creditor-history-labels'

export const columnsForRemovalInReducedView = ['stationRecordNumber', 'accountSalesDate']

interface CreditorHistoryTableProps {
  data: CreditorHistoryDetail[]
  renderKey?: string
  sumOfGrossProceedsExGST?: number
  sumOfNetProceedsGSTInclusive?: number
  sumOfTotalBales?: number
  sumOfTotalNetKg?: number
  noDataMessage?: string
  isLoading?: boolean
  isError?: boolean
  reducedView?: boolean
}

export const CreditorHistoryTable = ({
  data = [],
  renderKey = '',
  sumOfGrossProceedsExGST,
  sumOfNetProceedsGSTInclusive,
  sumOfTotalBales,
  sumOfTotalNetKg,
  noDataMessage = 'No creditor history available for this station',
  isLoading,
  isError,
  reducedView = false,
}: CreditorHistoryTableProps) => {
  const theme = useMantineTheme()

  const columns = useMemo<MRT_ColumnDef<CreditorHistoryDetail>[]>(() => {
    const allColumns: MRT_ColumnDef<CreditorHistoryDetail>[] = [
      {
        id: 'stationRecordNumber',
        header: keyToLabelMap.stationRecordNumber,
        accessorFn: (row) => `${row.stationRecordNumber} ${row.stationBrand}`,
        Footer: () => <>Totals</>,
      },
      {
        id: 'paymentDate',
        header: keyToLabelMap.paymentDate,
        accessorFn: (row) => (row.paymentDate == null ? null : new Date(row.paymentDate)),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY'),
      },
      {
        id: 'totalBalesInPayment',
        accessorKey: 'totalBalesInPayment',
        header: keyToLabelMap.totalBalesInPayment,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        Footer: () => (
          <NumberFormatter value={sumOfTotalBales ?? ''} thousandSeparator decimalScale={2} />
        ),
      },
      {
        id: 'totalNetKgInPayment',
        accessorKey: 'totalNetKgInPayment',
        header: keyToLabelMap.totalNetKgInPayment,
        Cell: ({ cell }) => (
          <NumberFormatter value={cell.getValue<number>() ?? ''} thousandSeparator />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        Footer: () => (
          <NumberFormatter value={sumOfTotalNetKg ?? ''} thousandSeparator decimalScale={2} />
        ),
      },
      {
        id: 'saleAndSeason',
        accessorKey: 'saleAndSeason',
        header: keyToLabelMap.saleAndSeason,
      },
      {
        id: 'grossProceedsExGST',
        accessorKey: 'grossProceedsExGST',
        header: keyToLabelMap.grossProceedsExGST,
        Cell: ({ cell }) => (
          <NumberFormatter
            prefix='$'
            value={cell.getValue<number>() ?? ''}
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
          />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        Footer: () => (
          <NumberFormatter
            prefix='$'
            value={sumOfGrossProceedsExGST ?? ''}
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
          />
        ),
      },
      {
        id: 'netProceedsGSTInclusive',
        accessorKey: 'netProceedsGSTInclusive',
        header: keyToLabelMap.netProceedsGSTInclusive,
        Cell: ({ cell }) => (
          <NumberFormatter
            prefix='$'
            value={cell.getValue<number>() ?? ''}
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
          />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        Footer: () => (
          <NumberFormatter
            prefix='$'
            value={sumOfNetProceedsGSTInclusive ?? ''}
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
          />
        ),
      },
      {
        id: 'accountSalesDate',
        header: keyToLabelMap.accountSalesDate,
        accessorFn: (row) => (row.accountSalesDate == null ? null : new Date(row.accountSalesDate)),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY'),
      },
    ]

    let filteredColumns = allColumns
    if (reducedView) {
      filteredColumns = allColumns.filter((column) => {
        // Only use id for filtering
        return !columnsForRemovalInReducedView.includes(column.id as string)
      })
    }
    return filteredColumns
  }, [
    sumOfGrossProceedsExGST,
    sumOfNetProceedsGSTInclusive,
    sumOfTotalBales,
    sumOfTotalNetKg,
    reducedView,
  ])

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data,
    state: {
      isLoading,
      showAlertBanner: isError,
    },
    mantineToolbarAlertBannerProps: tableDefaultAlertBannerProps(isError),
    renderEmptyRowsFallback: () => <NoDataMessage noDataMessage={noDataMessage} />,
    mantineTableProps: {
      ...tableDefaultProps.mantineTableProps,
      'aria-label': 'Creditor History',
    },
    mantineTableBodyRowProps: ({ row }) => {
      const paymentDate: string | null = row.getValue('paymentDate')
      if (!paymentDate) {
        return {}
      }

      const futureDate = new Date(paymentDate) > new Date()
      return futureDate ? { bg: theme.colors.yellow[0] } : {}
    },
  })

  return <MantineReactTable table={table} key={`${renderKey}-${reducedView}`} />
}
